import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  UserCheck,
  Plus,
  Calendar,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowRight,
  User,
  Shield,
  Edit2,
} from 'lucide-react';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';

export default function Delegaciones() {
  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [myDelegation, setMyDelegation] = useState(null);
  const [receivingDelegations, setReceivingDelegations] = useState([]);
  const [pendingDelegations, setPendingDelegations] = useState([]);
  
  // Form state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    usuario_destino_id: '',
    fecha_inicio: '',
    fecha_fin: '',
    motivo: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Accept/Reject dialog
  const [showAcceptRejectDialog, setShowAcceptRejectDialog] = useState(false);
  const [selectedPendingDelegation, setSelectedPendingDelegation] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/delegations/sync');
      const stats = res.data.stats;
      if (stats.activadas > 0 || stats.desactivadas > 0) {
        toast.success(`Sincronizado: ${stats.activadas} activadas, ${stats.desactivadas} desactivadas`);
      } else {
        toast.info('Delegaciones sincronizadas, sin cambios');
      }
      loadData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Solo administradores pueden sincronizar');
      } else {
        toast.error('Error al sincronizar');
      }
    } finally {
      setSyncing(false);
    }
  };

  const getDelegationStatus = (delegation) => {
    const now = new Date().toISOString();
    const fechaInicio = delegation.fecha_inicio;
    const fechaFin = delegation.fecha_fin;
    
    if (delegation.fecha_cancelacion) {
      return { status: 'CANCELADA', color: 'bg-red-100 text-red-700', icon: AlertCircle };
    }
    if (delegation.estado === 'RECHAZADA') {
      return { status: 'RECHAZADA', color: 'bg-red-100 text-red-700', icon: AlertCircle };
    }
    if (delegation.estado === 'PENDIENTE_ACEPTACION') {
      return { status: 'PENDIENTE ACEPTACIÓN', color: 'bg-purple-100 text-purple-700', icon: Clock };
    }
    if (fechaFin < now) {
      return { status: 'EXPIRADA', color: 'bg-gray-100 text-gray-700', icon: Clock };
    }
    if (fechaInicio <= now) {
      return { status: 'ACTIVA', color: 'bg-green-100 text-green-700', icon: CheckCircle };
    }
    return { status: 'PENDIENTE', color: 'bg-amber-100 text-amber-700', icon: Clock };
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [delegRes, usersRes, myRes, pendingRes] = await Promise.all([
        api.get('/delegations?activas=true'),
        api.get('/delegations/usuarios/disponibles'),
        api.get('/delegations/my-active'),
        api.get('/delegations/pending-acceptance').catch(() => ({ data: [] })),
      ]);
      
      setDelegations(delegRes.data || []);
      setUsers(usersRes.data || []);
      setMyDelegation(myRes.data?.delegando);
      setReceivingDelegations(myRes.data?.recibiendo || []);
      setPendingDelegations(pendingRes.data || []);
    } catch (error) {
      toast.error('Error al cargar delegaciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async () => {
    if (!formData.usuario_destino_id || !formData.fecha_inicio || !formData.fecha_fin || !formData.motivo) {
      toast.error('Todos los campos son requeridos');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/delegations', {
        ...formData,
        fecha_inicio: new Date(formData.fecha_inicio).toISOString(),
        fecha_fin: new Date(formData.fecha_fin).toISOString(),
      });
      toast.success('Delegación creada exitosamente');
      setShowCreateDialog(false);
      setFormData({ usuario_destino_id: '', fecha_inicio: '', fecha_fin: '', motivo: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear delegación');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (delegationId) => {
    if (!confirm('¿Estás seguro de cancelar esta delegación?')) return;
    
    try {
      await api.post(`/delegations/${delegationId}/cancel`);
      toast.success('Delegación cancelada');
      loadData();
    } catch (error) {
      toast.error('Error al cancelar delegación');
    }
  };

  const handleAcceptDelegation = async (delegationId) => {
    setSubmitting(true);
    try {
      await api.post(`/delegations/${delegationId}/accept`);
      toast.success('Delegación aceptada. Ahora recibirás las tareas delegadas.');
      setShowAcceptRejectDialog(false);
      setSelectedPendingDelegation(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aceptar delegación');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectDelegation = async (delegationId) => {
    if (!rejectReason.trim()) {
      toast.error('Por favor, indica el motivo del rechazo');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/delegations/${delegationId}/reject`, { motivo: rejectReason });
      toast.success('Delegación rechazada. Se notificará al solicitante.');
      setShowAcceptRejectDialog(false);
      setSelectedPendingDelegation(null);
      setRejectReason('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar delegación');
    } finally {
      setSubmitting(false);
    }
  };

  const openAcceptRejectDialog = (delegation) => {
    setSelectedPendingDelegation(delegation);
    setRejectReason('');
    setShowAcceptRejectDialog(true);
  };

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDelegation, setEditingDelegation] = useState(null);
  const [editForm, setEditForm] = useState({ fecha_inicio: '', fecha_fin: '', motivo: '' });

  const openEditDialog = (delegation) => {
    setEditingDelegation(delegation);
    setEditForm({
      fecha_inicio: delegation.fecha_inicio?.split('T')[0] || '',
      fecha_fin: delegation.fecha_fin?.split('T')[0] || '',
      motivo: delegation.motivo || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async () => {
    if (!editingDelegation) return;
    
    try {
      await api.put(`/delegations/${editingDelegation.id}`, editForm);
      toast.success('Delegación actualizada');
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <UserCheck className="text-slate-600" />
            Delegación de Aprobaciones
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Delega tus aprobaciones a otro usuario durante tu ausencia
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={handleSync}
            variant="outline"
            data-testid="sync-delegations-btn"
          >
            <RefreshCw size={16} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sincronizar
          </Button>
          <Button
            onClick={() => setShowCreateDialog(true)}
            disabled={!!myDelegation}
            data-testid="new-delegation-btn"
          >
            <Plus size={16} className="mr-2" />
            Nueva Delegación
          </Button>
        </div>
      </div>

      {/* Mi delegación activa */}
      {myDelegation && (
        <Card className="p-4 mb-6 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Clock className="text-amber-600" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">Delegación Activa</h3>
              <p className="text-sm text-amber-700 mt-1">
                Tus aprobaciones están siendo delegadas a <strong>{myDelegation.usuario_destino_nombre}</strong>
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Desde {formatDate(myDelegation.fecha_inicio)} hasta {formatDate(myDelegation.fecha_fin)}
              </p>
              <p className="text-xs text-amber-600">Motivo: {myDelegation.motivo}</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEditDialog(myDelegation)}
                className="text-blue-700 border-blue-300"
              >
                <Edit2 size={14} className="mr-1" />
                Modificar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCancel(myDelegation.id)}
                className="text-red-700 border-red-300"
              >
                <Trash2 size={14} className="mr-1" />
                Cancelar
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Delegaciones que estoy recibiendo */}
      {receivingDelegations.length > 0 && (
        <Card className="p-4 mb-6 bg-green-50 border-green-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <Shield className="text-green-600" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-green-800">Recibiendo Delegaciones</h3>
              {receivingDelegations.map((d) => (
                <p key={d.id} className="text-sm text-green-700 mt-1">
                  <strong>{d.usuario_origen_nombre}</strong> te delegó sus aprobaciones
                  <span className="text-xs text-green-600 ml-2">
                    ({formatDate(d.fecha_inicio)} - {formatDate(d.fecha_fin)})
                  </span>
                </p>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Delegaciones pendientes de mi aceptación */}
      {pendingDelegations.length > 0 && (
        <Card className="p-4 mb-6 bg-purple-50 border-purple-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="text-purple-600" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                Solicitudes de Delegación Pendientes
                <Badge className="bg-purple-600 text-white">{pendingDelegations.length}</Badge>
              </h3>
              <p className="text-sm text-purple-600 mb-3">
                Otros usuarios te han solicitado que recibas sus aprobaciones
              </p>
              <div className="space-y-3">
                {pendingDelegations.map((d) => (
                  <div key={d.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-purple-200">
                    <div>
                      <p className="font-medium text-slate-800">{d.usuario_origen_nombre}</p>
                      <p className="text-sm text-slate-500">
                        {formatDate(d.fecha_inicio)} - {formatDate(d.fecha_fin)}
                      </p>
                      <p className="text-sm text-slate-600">Motivo: {d.motivo}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => openAcceptRejectDialog(d)}
                        style={{ backgroundColor: '#7c3aed', color: 'white' }}
                      >
                        <CheckCircle size={14} className="mr-1" />
                        Revisar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Lista de todas las delegaciones activas */}
      <Card className="bg-white">
        <div className="p-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">Delegaciones Activas en el Sistema</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario Origen</TableHead>
              <TableHead></TableHead>
              <TableHead>Usuario Destino</TableHead>
              <TableHead>Período</TableHead>
              <TableHead>Motivo</TableHead>
              <TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {delegations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                  No hay delegaciones activas
                </TableCell>
              </TableRow>
            ) : (
              delegations.map((delegation) => (
                <TableRow key={delegation.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-800">{delegation.usuario_origen_nombre}</p>
                        <p className="text-xs text-slate-500">{delegation.usuario_origen_email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <ArrowRight size={16} className="text-slate-400" />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <UserCheck size={16} className="text-green-500" />
                      <div>
                        <p className="font-medium text-slate-800">{delegation.usuario_destino_nombre}</p>
                        <p className="text-xs text-slate-500">{delegation.usuario_destino_email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p>{formatDate(delegation.fecha_inicio)}</p>
                      <p className="text-slate-500">hasta {formatDate(delegation.fecha_fin)}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-600">{delegation.motivo}</span>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const { status, color, icon: StatusIcon } = getDelegationStatus(delegation);
                      return (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${color}`}>
                          <StatusIcon size={12} />
                          {status}
                        </span>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="text-blue-600" />
              Nueva Delegación
            </DialogTitle>
            <DialogDescription>
              Selecciona un usuario para que reciba tus aprobaciones durante tu ausencia
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Delegar a
              </label>
              <Select
                value={formData.usuario_destino_id}
                onValueChange={(val) => setFormData({ ...formData, usuario_destino_id: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar usuario..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.nombre || user.email} ({user.rol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Fecha Inicio
                </label>
                <Input
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Fecha Fin
                </label>
                <Input
                  type="date"
                  value={formData.fecha_fin}
                  onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Motivo
              </label>
              <Input
                placeholder="Ej: Vacaciones, Licencia médica..."
                value={formData.motivo}
                onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={submitting}>
              {submitting ? 'Creando...' : 'Crear Delegación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="text-blue-600" size={20} />
              Modificar Delegación
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha Inicio</Label>
                <Input
                  type="date"
                  value={editForm.fecha_inicio}
                  onChange={(e) => setEditForm({ ...editForm, fecha_inicio: e.target.value })}
                />
              </div>
              <div>
                <Label>Fecha Fin</Label>
                <Input
                  type="date"
                  value={editForm.fecha_fin}
                  onChange={(e) => setEditForm({ ...editForm, fecha_fin: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Motivo</Label>
              <Input
                placeholder="Ej: Vacaciones, Licencia médica..."
                value={editForm.motivo}
                onChange={(e) => setEditForm({ ...editForm, motivo: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdate} style={{ backgroundColor: '#2563eb', color: '#fff' }}>
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accept/Reject Dialog */}
      <Dialog open={showAcceptRejectDialog} onOpenChange={setShowAcceptRejectDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="text-purple-600" size={20} />
              Solicitud de Delegación
            </DialogTitle>
            <DialogDescription>
              {selectedPendingDelegation?.usuario_origen_nombre} te solicita recibir sus aprobaciones
            </DialogDescription>
          </DialogHeader>
          
          {selectedPendingDelegation && (
            <div className="py-4">
              <Card className="p-4 bg-slate-50">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Solicitante:</span>
                    <span className="font-medium">{selectedPendingDelegation.usuario_origen_nombre}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Período:</span>
                    <span className="font-medium">
                      {formatDate(selectedPendingDelegation.fecha_inicio)} - {formatDate(selectedPendingDelegation.fecha_fin)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Motivo:</span>
                    <span className="font-medium">{selectedPendingDelegation.motivo}</span>
                  </div>
                </div>
              </Card>

              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> Al aceptar, recibirás todas las solicitudes de aprobación 
                  asignadas a {selectedPendingDelegation.usuario_origen_nombre} durante el período indicado.
                </p>
              </div>

              <div className="mt-4">
                <Label>Motivo del rechazo (solo si rechazas)</Label>
                <Textarea
                  placeholder="Explica por qué no puedes aceptar esta delegación..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowAcceptRejectDialog(false)}>
              Cancelar
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleRejectDelegation(selectedPendingDelegation?.id)}
              disabled={submitting}
              style={{ color: '#dc2626', borderColor: '#fecaca' }}
            >
              <AlertCircle size={16} className="mr-1" />
              {submitting ? 'Rechazando...' : 'Rechazar'}
            </Button>
            <Button 
              onClick={() => handleAcceptDelegation(selectedPendingDelegation?.id)}
              disabled={submitting}
              style={{ backgroundColor: '#22c55e', color: 'white' }}
            >
              <CheckCircle size={16} className="mr-1" />
              {submitting ? 'Aceptando...' : 'Aceptar Delegación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
